import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { fetchSubscriptions, cancelSubscription, createSubscription } from '../store/slices/subscriptionsSlice';
import { RootState, AppDispatch } from '../store';
import { Subscription } from '../types';
import { SUBSCRIPTION_STATUS } from '../constants';
import api from '../services/api';

const Subscriptions: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { subscriptions, loading, totalCount, currentPage, pageSize, error } = useSelector(
    (state: RootState) => state.subscriptions
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [formData, setFormData] = useState({
    customerId: 'test_customer',
    productId: 'premium_plan',
    productName: 'テスト用プレミアムプラン',
    amount: '1500',
    billingCycle: '10minutes',
    currency: 'JPY',
    description: 'テスト用サブスクリプション決済',
    contactEmail: 'test@example.com',
    contactSms: '+81-90-1234-5678',
  });
  
  // Message handling from navigation state
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'warning' | 'error' } | null>(null);
  
  useEffect(() => {
    // Check for messages from navigation state (e.g., from card registration completion)
    if (location.state && (location.state as any).message) {
      const state = location.state as any;
      setMessage({
        text: state.message,
        type: state.type || 'success'
      });
      
      // Clear the location state to prevent message from persisting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;
    if (statusFilter) filters.status = statusFilter;

    dispatch(fetchSubscriptions({ 
      page: page + 1, 
      pageSize: rowsPerPage, 
      filters 
    }));
    
  }, [dispatch, page, rowsPerPage, searchTerm, statusFilter]);

  const handleSearch = () => {
    setPage(0);
    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;
    if (statusFilter) filters.status = statusFilter;

    dispatch(fetchSubscriptions({ 
      page: 1, 
      pageSize: rowsPerPage, 
      filters 
    }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateClick = () => {
    setFormData({
      customerId: 'test_customer',
      productId: 'premium_plan',
      productName: 'テスト用プレミアムプラン',
      amount: '1500',
      billingCycle: '10minutes',
      currency: 'JPY',
      description: 'テスト用サブスクリプション決済',
      contactEmail: 'test@example.com',
      contactSms: '+81-90-1234-5678',
    });
    
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (formData.productName && formData.amount) {
      const subscriptionData = {
        customerId: formData.customerId,
        productId: formData.productId,
        productName: formData.productName,
        amount: parseInt(formData.amount),
        billingCycle: formData.billingCycle as 'monthly' | 'yearly' | 'weekly' | 'daily' | '10minutes' | '1hour' | 'immediate',
        currency: formData.currency,
        description: formData.description,
        contactEmail: formData.contactEmail,
        contactSms: formData.contactSms,
      };
      try {
        const result = await dispatch(createSubscription(subscriptionData));
        console.log('Full subscription result:', result);
        console.log('Result payload:', result.payload);
        console.log('Result type:', result.type);
        
        // Check if the action was fulfilled
        if (result.type === 'subscriptions/createSubscription/fulfilled') {
          setCreateDialogOpen(false);
          
          // Check if there's a redirectUrl to go to eSCOTT card registration
          if (result.payload && result.payload.redirectUrl) {
            console.log('Redirecting to eSCOTT card registration:', result.payload.redirectUrl);
            
            // Immediate redirect to eSCOTT (external URL)
            window.location.href = result.payload.redirectUrl;
            return;
          } else {
            console.log('No redirectUrl found in result.payload');
          }
        } else {
          console.log('Subscription creation failed or was rejected');
          setCreateDialogOpen(false);
        }
        
        // Refresh the list if no redirect
        const filters: any = {};
        if (searchTerm) filters.search = searchTerm;
        if (statusFilter) filters.status = statusFilter;
        dispatch(fetchSubscriptions({ 
          page: page + 1, 
          pageSize: rowsPerPage, 
          filters 
        }));
      } catch (error) {
        console.error('Error creating subscription:', error);
      }
    }
  };

  const handleCancelClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (selectedSubscription) {
      try {
        await dispatch(cancelSubscription(selectedSubscription.subscriptionId)).unwrap();
        setCancelDialogOpen(false);
        setSelectedSubscription(null);
        setMessage({ text: 'サブスクリプションをキャンセルしました', type: 'success' });
        // Refresh the list
        const filters: any = {};
        if (searchTerm) filters.search = searchTerm;
        if (statusFilter) filters.status = statusFilter;
        dispatch(fetchSubscriptions({ 
          page: page + 1, 
          pageSize: rowsPerPage, 
          filters 
        }));
      } catch (error: any) {
        setCancelDialogOpen(false);
        setSelectedSubscription(null);
        setMessage({ 
          text: error.message || 'サブスクリプションのキャンセルに失敗しました', 
          type: 'error' 
        });
      }
    }
  };

  const handleManualPaymentClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setPaymentAmount(subscription.amount?.toString() || '');
    setPaymentDialogOpen(true);
  };

  const handleManualPaymentConfirm = async () => {
    if (!selectedSubscription) return;

    try {
      setMessage({ text: '手動決済を実行中...', type: 'warning' });
      setPaymentDialogOpen(false);

      const response = await api.post(`/subscriptions/${selectedSubscription.subscriptionId}/pay`, {
        amount: parseInt(paymentAmount),
        description: `手動決済 - サブスクリプション ${selectedSubscription.subscriptionId}`
      });

      const result = response.data;

      if (result.success) {
        setMessage({ 
          text: `手動決済が完了しました。取引ID: ${result.data.transactionId} / 承認番号: ${result.data.approveNo}`, 
          type: 'success' 
        });
        
        // Refresh the subscription list
        const filters: any = {};
        if (searchTerm) filters.search = searchTerm;
        if (statusFilter) filters.status = statusFilter;
        dispatch(fetchSubscriptions({ 
          page: page + 1, 
          pageSize: rowsPerPage, 
          filters 
        }));
      } else {
        setMessage({ 
          text: `手動決済に失敗しました: ${result.error}`, 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Manual payment error:', error);
      setMessage({ 
        text: '手動決済の実行中にエラーが発生しました', 
        type: 'error' 
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case SUBSCRIPTION_STATUS.ACTIVE:
        return 'success';
      case SUBSCRIPTION_STATUS.PENDING_CARD_REGISTRATION:
        return 'warning';
      case SUBSCRIPTION_STATUS.PAST_DUE:
        return 'error';
      case SUBSCRIPTION_STATUS.CANCELLED:
        return 'default';
      case SUBSCRIPTION_STATUS.PAUSED:
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case SUBSCRIPTION_STATUS.ACTIVE:
        return '利用中';
      case SUBSCRIPTION_STATUS.PENDING_CARD_REGISTRATION:
        return 'カード登録待ち';
      case SUBSCRIPTION_STATUS.PAST_DUE:
        return '支払い遅延';
      case SUBSCRIPTION_STATUS.CANCELLED:
        return '解約済み';
      case SUBSCRIPTION_STATUS.PAUSED:
        return '一時停止';
      default:
        return status;
    }
  };

  const canCancel = (subscription: Subscription) => {
    return subscription.status === SUBSCRIPTION_STATUS.ACTIVE || 
           subscription.status === SUBSCRIPTION_STATUS.PENDING_CARD_REGISTRATION ||
           subscription.status === SUBSCRIPTION_STATUS.PAST_DUE;
  };


  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };
  
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        サブスクリプション管理
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="検索（顧客ID、サブスクリプションID）"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>ステータス</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="ステータス"
              >
                <MenuItem value="">すべて</MenuItem>
                <MenuItem value={SUBSCRIPTION_STATUS.PENDING_CARD_REGISTRATION}>カード登録待ち</MenuItem>
                <MenuItem value={SUBSCRIPTION_STATUS.ACTIVE}>利用中</MenuItem>
                <MenuItem value={SUBSCRIPTION_STATUS.PAST_DUE}>支払い遅延</MenuItem>
                <MenuItem value={SUBSCRIPTION_STATUS.PAUSED}>一時停止</MenuItem>
                <MenuItem value={SUBSCRIPTION_STATUS.CANCELLED}>解約済み</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              検索
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              disabled={loading}
            >
              新規サブスクリプション
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>サブスクリプションID</TableCell>
                  <TableCell>顧客ID</TableCell>
                  <TableCell>金額</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>開始日</TableCell>
                  <TableCell>次回請求日</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (!subscriptions || subscriptions.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (!subscriptions || subscriptions.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      サブスクリプションデータがありません
                    </TableCell>
                  </TableRow>
                ) : (
                  subscriptions.map((subscription: Subscription) => (
                    <TableRow key={subscription.id} hover>
                      <TableCell>{subscription.id}</TableCell>
                      <TableCell>{subscription.subscriptionId}</TableCell>
                      <TableCell>{subscription.customerId || '-'}</TableCell>
                      <TableCell>{formatAmount(subscription.amount || 0, subscription.currency || 'JPY')}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(subscription.status)}
                          color={getStatusColor(subscription.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {subscription.startDate 
                          ? new Date(subscription.startDate).toLocaleDateString('ja-JP')
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        {subscription.nextChargeDate 
                          ? new Date(subscription.nextChargeDate).toLocaleDateString('ja-JP')
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {canCancel(subscription) && (
                            <Button
                              size="small"
                              startIcon={<CancelIcon />}
                              onClick={() => handleCancelClick(subscription)}
                              color="error"
                              disabled={loading}
                            >
                              キャンセル
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => handleManualPaymentClick(subscription)}
                            sx={{ minWidth: 120 }}
                          >
                            💳 手動決済
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} / ${count !== -1 ? count : `${to}以上`}`
            }
          />
        </Paper>

        {/* Create Subscription Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            🧪 デバッグ用サブスクリプション作成
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              eSCOTT連携テスト用の詳細設定
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {/* 基本設定 */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                📋 基本設定
              </Typography>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="顧客ID *"
                    value={formData.customerId}
                    onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                    margin="normal"
                    required
                    helperText="デバッグ用の自動生成ID"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="金額 *"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    margin="normal"
                    required
                    helperText="決済金額（円）"
                    InputProps={{
                      endAdornment: <Typography variant="body2" color="text.secondary">円</Typography>
                    }}
                  />
                </Grid>
              </Grid>

              {/* 決済タイミング設定 */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                ⏰ 決済タイミング設定
              </Typography>
              
              <FormControl fullWidth margin="normal" required>
                <InputLabel>課金サイクル（決済実行タイミング）</InputLabel>
                <Select
                  value={formData.billingCycle}
                  onChange={(e) => setFormData({ ...formData, billingCycle: e.target.value })}
                  label="課金サイクル（決済実行タイミング）"
                >
                  <MenuItem value="immediate">🚀 即座（30秒後） - テスト用</MenuItem>
                  <MenuItem value="10minutes">⏱️ 10分後 - デバッグ推奨</MenuItem>
                  <MenuItem value="1hour">🕐 1時間後</MenuItem>
                  <MenuItem value="daily">📅 日次（24時間後）</MenuItem>
                  <MenuItem value="weekly">📊 週次（7日後）</MenuItem>
                  <MenuItem value="monthly">📆 月次（1ヶ月後）</MenuItem>
                  <MenuItem value="yearly">🗓️ 年次（1年後）</MenuItem>
                </Select>
              </FormControl>

              {/* 顧客情報 */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                👤 顧客情報（eSCOTT連携用）
              </Typography>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="プロダクトID"
                    value={formData.productId}
                    onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                    margin="normal"
                    helperText="サブスクリプションプロダクトの識別子"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="メールアドレス"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    margin="normal"
                    helperText="通知用メールアドレス"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="SMS番号"
                    value={formData.contactSms}
                    onChange={(e) => setFormData({ ...formData, contactSms: e.target.value })}
                    margin="normal"
                    helperText="SMS通知用電話番号"
                  />
                </Grid>
              </Grid>

              {/* eSCOTT連携設定 */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                🔗 eSCOTT連携設定
              </Typography>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>通貨</InputLabel>
                    <Select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      label="通貨"
                    >
                      <MenuItem value="JPY">🇯🇵 JPY (日本円)</MenuItem>
                      <MenuItem value="USD">🇺🇸 USD (米ドル)</MenuItem>
                      <MenuItem value="EUR">🇪🇺 EUR (ユーロ)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth
                    label="決済説明"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    margin="normal"
                    helperText="eSCOTTの決済画面に表示される説明文"
                  />
                </Grid>
              </Grid>


              {/* プレビュー */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                👀 設定プレビュー
              </Typography>
              
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>決済予定:</strong> {formData.amount}円 ({formData.currency}) - {
                    formData.billingCycle === 'immediate' ? '30秒後' :
                    formData.billingCycle === '10minutes' ? '10分後' :
                    formData.billingCycle === '1hour' ? '1時間後' :
                    formData.billingCycle === 'daily' ? '24時間後' :
                    formData.billingCycle === 'weekly' ? '7日後' :
                    formData.billingCycle === 'monthly' ? '1ヶ月後' :
                    formData.billingCycle === 'yearly' ? '1年後' : '不明'
                  }
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>プロダクト:</strong> {formData.productName}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>連絡先:</strong> {formData.contactEmail} / {formData.contactSms}
                </Typography>
                <Typography variant="body2">
                  <strong>説明:</strong> {formData.description}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setCreateDialogOpen(false)}
              size="large"
            >
              キャンセル
            </Button>
            <Button 
              onClick={handleCreateSubmit} 
              variant="contained"
              size="large"
              disabled={!formData.customerId || !formData.amount}
              startIcon={<span>🚀</span>}
              sx={{ minWidth: 200 }}
            >
              デバッグテスト開始
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Confirmation Dialog */}
        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
          <DialogTitle>サブスクリプションキャンセル確認</DialogTitle>
          <DialogContent>
            <Typography>
              サブスクリプション「{selectedSubscription?.subscriptionId}」をキャンセルしますか？
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              この操作は取り消せません。
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>
              キャンセル
            </Button>
            <Button 
              onClick={handleCancelConfirm} 
              color="error" 
              variant="contained"
              disabled={loading}
            >
              {loading ? '処理中...' : 'キャンセル実行'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Manual Payment Dialog */}
        <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            💳 手動決済
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              即座にeSCOTT決済を実行します
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {selectedSubscription && (
                <>
                  <Typography variant="h6" gutterBottom>
                    サブスクリプション情報
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>ID:</strong> {selectedSubscription.id}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>サブスクリプションID:</strong> {selectedSubscription.subscriptionId}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>顧客ID:</strong> {selectedSubscription.customerId || '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>現在の金額:</strong> {formatAmount(selectedSubscription.amount || 0, selectedSubscription.currency || 'JPY')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>ステータス:</strong> {getStatusLabel(selectedSubscription.status)}
                    </Typography>
                  </Box>

                  <Typography variant="h6" gutterBottom>
                    決済設定
                  </Typography>
                  <TextField
                    fullWidth
                    label="決済金額"
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    margin="normal"
                    required
                    helperText="決済する金額を入力してください（デフォルト: サブスクリプション設定金額）"
                    InputProps={{
                      endAdornment: <Typography variant="body2" color="text.secondary">円</Typography>
                    }}
                  />

                  <Box sx={{ bgcolor: 'info.light', p: 2, borderRadius: 1, mt: 2 }}>
                    <Typography variant="body2" color="info.dark">
                      <strong>📝 決済について</strong>
                    </Typography>
                    <Typography variant="body2" color="info.dark" sx={{ mt: 1 }}>
                      • eSCOTT 1Gathering（売上同時計上）で即座に決済処理
                    </Typography>
                    <Typography variant="body2" color="info.dark">
                      • 登録済みカード情報を使用して決済実行
                    </Typography>
                    <Typography variant="body2" color="info.dark">
                      • 決済完了後、承認番号と取引IDが発行されます
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setPaymentDialogOpen(false)} size="large">
              キャンセル
            </Button>
            <Button 
              onClick={handleManualPaymentConfirm} 
              variant="contained"
              color="primary"
              size="large"
              disabled={!paymentAmount || parseInt(paymentAmount) <= 0}
              startIcon={<span>💳</span>}
              sx={{ minWidth: 180 }}
            >
              手動決済実行
            </Button>
          </DialogActions>
        </Dialog>

        {/* Message Snackbar */}
        <Snackbar
          open={!!message}
          autoHideDuration={6000}
          onClose={() => setMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setMessage(null)} 
            severity={message?.type || 'info'}
            sx={{ width: '100%' }}
          >
            {message?.text}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default Subscriptions;